import { useEffect, useMemo, useRef, useState } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconMapPin, IconPlus, IconSearch, IconX } from '@tabler/icons-react'
import { LAB_VENDOR_PARENT_NAME } from '@/model/middleware-types'
import { useDebounce } from '@/hooks/useDebounce'
import { RADIOLOGY_MODALITY_OPTIONS } from '../radiologyFormData'
import { InlineAddFacilityForm } from '../RadiologyFacilityPicker'
import { InlineAddLabFacilityForm } from '../shared/InlineAddLabFacilityForm'
import { usePatientGeocode } from '../shared/usePatientGeocode'
import { getErrorMessage } from '../shared/errorUtils'
import { FacilityRow } from './FacilityRow'
import { FacilityMap } from './FacilityMap'
import { useAdoptFacility } from './useAdoptFacility'
import { useEnrichmentPolling, mergeEnrichment } from './useEnrichmentPolling'
import { useFacilityDiscovery } from './useFacilityDiscovery'
import {
  useBookmarkFacility,
  useFacilityFrequent,
  useRememberFacility,
} from './useFacilityFavorites'
import {
  scrollTopForRow,
  toFacilityForOrder,
  toLabFacilityForOrder,
  type DiscoveredFacility,
  type DiscoveryModality,
  type FacilityForOrder,
  type LabFacilityForOrder,
  type LabVendor,
} from './discoveryTypes'

// Design 1a: "Recommended" is the server ranking (previously used first, then
// distance); the other two are client-side re-sorts of the same result set.
type SortKey = 'recommended' | 'distance' | 'rating'
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'distance', label: 'Distance' },
  { value: 'rating', label: 'Rating' },
]

/** A copy of `set` with `id` added or removed — state sets are never mutated in place. */
function withId(set: Set<string>, id: string, present: boolean): Set<string> {
  const next = new Set(set)
  if (present) next.add(id)
  else next.delete(id)
  return next
}

export const GEOCODE_NOTICE_TITLE = 'Patient address could not be located'

type CommonProps = {
  patient: Patient
  selectedFacilityId: string
  /** PD-1938 / PD-1939 — offered inside a selected row that has no fax. */
  onPatientChoosesLocation?: () => void
  /** The fax the order currently goes to, shown on the selected facility's row. */
  selectedFacilityFax?: string
}

// A discriminated pair rather than one loose signature: the two order flows
// consume different facility shapes, and a lab search is meaningless without a
// vendor — both are mistakes the caller should not be able to make.
export type FacilityDiscoveryProps =
  | (CommonProps & {
      kind: 'radiology'
      vendor?: never
      /** Modality of the procedures chosen in step 1; undefined = general imaging search. */
      modality?: DiscoveryModality
      onSelectFacility: (organizationId: string, facility: FacilityForOrder | null) => void
    })
  | (CommonProps & {
      kind: 'lab'
      vendor: LabVendor
      modality?: never
      onSelectFacility: (organizationId: string, facility: LabFacilityForOrder | null) => void
    })

export const FacilityDiscovery: React.FC<FacilityDiscoveryProps> = (props) => {
  const { patient, selectedFacilityId } = props
  const isLab = props.kind === 'lab'
  const parentLabName = props.kind === 'lab' ? LAB_VENDOR_PARENT_NAME[props.vendor] : undefined
  const modality = props.kind === 'radiology' ? props.modality : undefined

  // 'state' searches the patient's whole state by facility name (radiology only).
  const [radius, setRadius] = useState<25 | 50 | 'state'>(25)
  // Radiology only: the practice's Frequently used list in place of the nearby search.
  const [view, setView] = useState<'nearby' | 'frequent'>('nearby')
  const [openOnly, setOpenOnly] = useState(false)
  const [faxOnly, setFaxOnly] = useState(false)
  const [sort, setSort] = useState<SortKey>('recommended')
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState<string | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)
  const [adoptingId, setAdoptingId] = useState<string | null>(null)
  const [rememberingIds, setRememberingIds] = useState<Set<string>>(() => new Set())
  // Place ids added this session: they count as remembered before the Frequently used
  // refetch lands, so the same facility cannot be added twice.
  const [addedIds, setAddedIds] = useState<Set<string>>(() => new Set())
  const [bookmarkingIds, setBookmarkingIds] = useState<Set<string>>(() => new Set())
  const debouncedQuery = useDebounce(query, 400)
  const listRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef(new Map<string, HTMLDivElement>())

  // A row expands in place when it is highlighted, which also happens from the map.
  // Without this the clicked pin's card can open far outside the list's scroll
  // window, leaving the clinician to hunt for it. The jump is deliberately not
  // `scroll-smooth`: the same click grows the row, and that relayout cancels an
  // in-flight smooth scroll, stranding the list part-way to the card.
  useEffect(() => {
    const list = listRef.current
    const row = highlighted ? rowRefs.current.get(highlighted) : undefined
    if (!list || !row) return
    list.scrollTop = scrollTopForRow(
      list.scrollTop,
      list.clientHeight,
      row.offsetTop,
      row.offsetHeight,
    )
  }, [highlighted])

  const {
    coords,
    patientState,
    loading: geoLoading,
    fallbackMode,
    fallbackReason,
  } = usePatientGeocode(patient, {
    zipOnly: true,
  })
  const addr = patient.address?.find((a) => a.use !== 'old')
  // Radiology sends no `kind` at all so its request is byte-for-byte what it was
  // before labs existed (the server defaults to radiology).
  const scope = props.kind === 'lab' ? { kind: 'lab' as const, vendor: props.vendor } : {}
  const trimmedQuery = debouncedQuery.trim()
  const frequentView = !isLab && view === 'frequent'
  const stateWide = !isLab && radius === 'state' && Boolean(patientState)
  const baseInput = {
    ...scope,
    // A state-wide search still sends a radius; the server ignores it for that scope.
    radiusMiles: radius === 'state' ? 25 : radius,
    modality,
    query: trimmedQuery || undefined,
    state: patientState ?? undefined,
  }
  // The server only searches a whole state by name — two characters at least.
  const awaitingStateQuery = stateWide && Boolean(coords) && trimmedQuery.length < 2
  // Geocoder down or ZIP unresolvable (fallbackMode): still ask for the saved in-state
  // facilities without coordinates instead of leaving the step empty. The state is the
  // only bound on that query, so without one there is nothing sensible to ask for.
  const coordinateFree = fallbackMode && Boolean(patientState)

  // Only a `not-found` answer is the patient's address at fault; a geocoder outage
  // (`unavailable`) is ours and must not send the clinician to fix Demographics.
  const addressNotFound = fallbackMode && fallbackReason === 'not-found'

  // The clinician has to know the list is the saved in-state one because the patient's
  // address could not be placed on the map — shown once per patient (the form can stay
  // mounted while the clinician switches patients).
  const geocodeNoticeShownFor = useRef<string | null>(null)
  useEffect(() => {
    if (!addressNotFound || coords || geocodeNoticeShownFor.current === patient.id) return
    geocodeNoticeShownFor.current = patient.id ?? null
    const where = [addr?.city, addr?.state].filter(Boolean).join(', ')
    modals.open({
      title: GEOCODE_NOTICE_TITLE,
      children: (
        <Stack gap="sm">
          <Text size="sm">
            We could not find map coordinates for {where ? `${where} ` : ''}
            {addr?.postalCode ?? ''}. Check the ZIP code and state under Demographics.
          </Text>
          <Text size="sm">
            {patientState
              ? `Until then, this list shows the saved facilities in ${patientState} without distances.`
              : 'Without a state there is nothing to search; you can still add the facility manually below.'}
          </Text>
          <Group justify="flex-end" mt="xs">
            <Button color="teal" onClick={() => modals.closeAll()}>
              Got it
            </Button>
          </Group>
        </Stack>
      ),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to the geocode outcome only
  }, [addressNotFound, coords, patient.id])
  // The coordinate-free legacy path has no state scope; it already lists the whole state.
  const search = useFacilityDiscovery(
    coords
      ? awaitingStateQuery
        ? null
        : {
            ...baseInput,
            lat: coords.lat,
            lon: coords.lon,
            ...(stateWide ? { scope: 'state' as const } : {}),
          }
      : coordinateFree
        ? baseInput
        : null,
  )
  // Loaded in either view: the nearby rows mark the facilities already in it. Held until
  // the geocode settles, so it is not fetched once without distances and again with them.
  const frequent = useFacilityFrequent(
    isLab || geoLoading ? null : coords ? { lat: coords.lat, lon: coords.lon } : {},
  )
  const frequentRows = useMemo(() => frequent.data?.facilities ?? [], [frequent.data])
  const rememberedIds = useMemo(() => {
    const ids = new Set(frequentRows.map((f) => f.placeId))
    for (const id of addedIds) ids.add(id)
    return ids
  }, [frequentRows, addedIds])
  const bookmarkedIds = useMemo(
    () => new Set(frequentRows.filter((f) => f.bookmarked).map((f) => f.placeId)),
    [frequentRows],
  )
  const facilities = useMemo(() => search.data?.facilities ?? [], [search.data])
  // Legacy fallback results have no enrichment job running server-side (see F3
  // in facility-discovery.service.ts) — polling them would just burn 60s of
  // requests for nothing.
  const isLegacySource = search.data?.source === 'legacy'
  // Only labs send a kind, so a radiology poll stays byte-for-byte what it was
  // before labs existed — same reasoning as the search request above.
  // Frequently used rows are not a search result either, so they are not polled.
  const views = useEnrichmentPolling(
    isLegacySource || frequentView ? [] : facilities,
    isLab ? 'lab' : undefined,
  )
  const adopt = useAdoptFacility()
  const remember = useRememberFacility()
  const bookmark = useBookmarkFacility()

  const visible = useMemo(() => {
    // The server already orders the list bookmarked first; the filters and sort are off.
    if (frequentView) return frequentRows
    // A favorite's saved fax fills a nearby row that found none.
    const savedFax = new Map(frequentRows.filter((f) => f.fax).map((f) => [f.placeId, f.fax]))
    const merged = facilities
      .map((f) => mergeEnrichment(f, views[f.placeId]))
      .map((f) =>
        !f.fax && savedFax.has(f.placeId)
          ? { ...f, fax: savedFax.get(f.placeId), faxSource: 'saved' as const }
          : f,
      )
      // Unknown hours (legacy rows, some Places results) do not count as open.
      .filter((f) => !openOnly || f.openNow === true)
      .filter((f) => !faxOnly || Boolean(f.fax))
    if (sort === 'distance')
      return [...merged].sort(
        (a, b) => (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity) || 0,
      )
    if (sort === 'rating') return [...merged].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
    return merged
  }, [facilities, views, openOnly, faxOnly, sort, frequentView, frequentRows])

  const handleSelected = (f: DiscoveredFacility, organizationId: string, fax?: string) => {
    if (props.kind === 'lab') {
      props.onSelectFacility(organizationId, toLabFacilityForOrder(f, organizationId, fax))
    } else {
      props.onSelectFacility(organizationId, toFacilityForOrder(f, organizationId, fax))
    }
  }

  const handleUse = async (placeId: string, fax: string | undefined, saveFax: boolean) => {
    const f = visible.find((x) => x.placeId === placeId)
    if (!f) return
    setAdoptingId(placeId)
    try {
      // PD-1996 — the fax goes on this order either way; it is written to the shared
      // facility record only when the provider asked to save it for future orders.
      const result = await adopt.mutateAsync({ ...scope, placeId, fax: saveFax ? fax : undefined })
      handleSelected(f, result.organizationId, fax ?? result.fax)
      setHighlighted(placeId)
      notifications.show({ title: 'Facility selected', message: f.name, color: 'green' })
      // A Frequently used row shows the fax its favorite was saved with, so a fax saved
      // here is written to the favorite too, or the next visit would show the old one.
      const savedFax = result.fax ?? fax
      if (saveFax && savedFax && rememberedIds.has(placeId)) {
        await remember.mutateAsync({
          placeId,
          organizationId: result.organizationId,
          fax: savedFax,
        })
      }
    } catch (err) {
      notifications.show({
        title: 'Could not select facility',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setAdoptingId(null)
    }
  }

  // Adding to Frequently used never selects the facility for this order. The favorite
  // points at an Organization, so the facility is adopted first — without the typed fax:
  // the Organization is shared across practices, so a typo here must not become everyone's
  // fax (PD-1996). The favorite carries the fax, and its row seeds the fax field from it.
  const handleRemember = async (placeId: string, fax: string) => {
    if (rememberingIds.has(placeId)) return
    setRememberingIds((prev) => withId(prev, placeId, true))
    try {
      const result = await adopt.mutateAsync({ placeId })
      await remember.mutateAsync({ placeId, organizationId: result.organizationId, fax })
      setAddedIds((prev) => withId(prev, placeId, true))
      notifications.show({ message: 'Added to frequently used', color: 'teal' })
    } catch (err) {
      notifications.show({
        title: 'Could not add to frequently used',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setRememberingIds((prev) => withId(prev, placeId, false))
    }
  }

  const handleBookmark = async (placeId: string, bookmarked: boolean) => {
    setBookmarkingIds((prev) => withId(prev, placeId, true))
    try {
      await bookmark.mutateAsync({ placeId, bookmarked })
    } catch (err) {
      notifications.show({
        title: 'Could not update the bookmark',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setBookmarkingIds((prev) => withId(prev, placeId, false))
    }
  }

  const openAddFacilityModal = () => {
    modals.open({
      size: 'lg',
      children:
        props.kind === 'lab' ? (
          <InlineAddLabFacilityForm
            parentLabName={LAB_VENDOR_PARENT_NAME[props.vendor]}
            onSave={(orgId, data) => {
              modals.closeAll()
              props.onSelectFacility(orgId, {
                id: orgId,
                name: data.name,
                address: data.address,
                city: data.city,
                state: data.state,
                zip: data.zip,
                fax: data.fax,
                lat: null,
                lon: null,
                // Pending approval keeps the order draft-only until an admin signs off.
                pendingApproval: true,
                isOrganization: true,
              })
              notifications.show({
                title: 'Facility Added',
                message: 'New facility added. Pending admin approval — you can save a draft order.',
                color: 'green',
              })
            }}
            onCancel={() => modals.closeAll()}
          />
        ) : (
          <InlineAddFacilityForm
            onSave={(orgId, data) => {
              modals.closeAll()
              props.onSelectFacility(orgId, {
                id: orgId,
                name: data.name,
                address: data.address,
                city: data.city,
                state: data.state,
                zip: data.zip,
                fax: data.fax,
                lat: null,
                lon: null,
                modalities: [],
                pendingApproval: true,
                isOrganization: true,
              } as unknown as FacilityForOrder)
              notifications.show({
                title: 'Facility Added',
                message: 'New facility added. Pending admin approval — you can save a draft order.',
                color: 'green',
              })
            }}
            onCancel={() => modals.closeAll()}
          />
        ),
    })
  }

  const loading = geoLoading || (frequentView ? frequent.isLoading : search.isLoading)
  // Coordinates or the coordinate-free fallback; only a patient with no address at all
  // has nothing to search with. The Frequently used list needs no address.
  const canSearch = Boolean(coords) || coordinateFree
  const showList = frequentView || canSearch
  const modalityLabel = RADIOLOGY_MODALITY_OPTIONS.find((m) => m.key === modality)?.label
  // What the result count is scoped to: the lab brand, or the chosen modality.
  const scopeLabel = parentLabName ?? modalityLabel

  return (
    <Paper p="md" withBorder>
      <div className="mb-3 flex flex-wrap items-baseline gap-3">
        <Group gap="xs">
          <IconMapPin size={20} className="text-[#0097CE]" />
          <Text fw={600} size="lg" className="text-[#0097CE]">
            {isLab ? 'SELECT LAB FACILITY' : 'SELECT RADIOLOGY FACILITY'}
          </Text>
        </Group>
        {frequentView ? (
          <Text size="sm" className="text-[#8C9AA8]">
            {frequent.data ? `${visible.length} frequently used` : 'Frequently used'}
          </Text>
        ) : (
          addr && (
            <Text size="sm" className="text-[#8C9AA8]">
              {scopeLabel ? `${scopeLabel} · ` : ''}
              {stateWide && coords ? (
                search.data ? (
                  `${visible.length} in ${patientState}`
                ) : (
                  `In ${patientState}`
                )
              ) : (
                <>
                  {search.data ? `${visible.length} near ` : 'Near '}
                  {[addr.city, addr.state].filter(Boolean).join(', ')} {addr.postalCode} · patient
                  home ZIP
                </>
              )}
            </Text>
          )
        )}
      </div>
      {!frequentView && search.data?.source === 'legacy' && (
        <Alert color="orange" mb="sm">
          {coordinateFree
            ? fallbackReason === 'not-found'
              ? `The patient's address could not be located on the map. Showing saved facilities in ${patientState}.`
              : `Location lookup is unavailable right now. Showing saved facilities in ${patientState}.`
            : 'Live facility search is unavailable. Showing saved facilities.'}
        </Alert>
      )}

      <div className="mb-3 flex gap-2.5">
        <TextInput
          className="flex-1"
          aria-label="Search facilities by name"
          placeholder="Search by facility name…"
          leftSection={<IconSearch size={16} />}
          value={query}
          disabled={frequentView}
          onChange={(e) => setQuery(e.currentTarget.value)}
          rightSection={
            query ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Clear search"
                onClick={() => setQuery('')}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
          rightSectionPointerEvents={query ? 'all' : 'none'}
        />
        {!frequentView && (
          <Select
            aria-label="Sort facilities"
            data={SORT_OPTIONS}
            value={sort}
            onChange={(v) => setSort((v as SortKey | null) ?? 'recommended')}
            allowDeselect={false}
            w={200}
            renderOption={({ option }) => <span>{option.label}</span>}
            leftSection={<span className="text-[13px] text-[#4A5A6A]">Sort</span>}
            leftSectionWidth={46}
          />
        )}
      </div>

      <Group gap="lg" className="mb-1 border-b border-[#E3E8EE] pb-3.5">
        {/* Without coordinates there is no radius to pick, and the coordinate-free
            fallback already lists the whole state. */}
        {!isLab && coords && (
          <SegmentedControl
            size="xs"
            aria-label="Search area"
            value={String(radius)}
            disabled={frequentView}
            onChange={(v) => setRadius(v === 'state' ? 'state' : v === '50' ? 50 : 25)}
            data={[
              { value: '25', label: '25 mi' },
              { value: '50', label: '50 mi' },
              ...(patientState ? [{ value: 'state', label: `All of ${patientState}` }] : []),
            ]}
          />
        )}
        <Switch
          size="sm"
          color="#103F6B"
          label="Open now"
          checked={openOnly}
          disabled={frequentView}
          onChange={(e) => setOpenOnly(e.currentTarget.checked)}
        />
        <Switch
          size="sm"
          color="#103F6B"
          label="Fax on file only"
          checked={faxOnly}
          disabled={frequentView}
          onChange={(e) => setFaxOnly(e.currentTarget.checked)}
        />
        {!isLab && (
          <Switch
            size="sm"
            color="#103F6B"
            label="Frequently used"
            checked={frequentView}
            onChange={(e) => setView(e.currentTarget.checked ? 'frequent' : 'nearby')}
          />
        )}
      </Group>

      {!frequentView && !canSearch && !geoLoading && (
        <Text size="sm" c="dimmed" py="sm">
          Add a patient address to search nearby facilities.
        </Text>
      )}
      {loading && (
        <Group gap="xs" py="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            {frequentView
              ? 'Loading frequently used facilities…'
              : 'Finding facilities near the patient…'}
          </Text>
        </Group>
      )}
      {!frequentView && search.isError && (
        <Alert color="red" my="sm">
          Facility search failed. Try again or add the facility manually.
        </Alert>
      )}
      {frequentView && frequent.isError && (
        <Alert color="red" my="sm">
          Frequently used facilities could not be loaded. Try again.
        </Alert>
      )}

      {showList && !loading && (
        <div className="grid gap-5 pt-3 lg:grid-cols-[minmax(0,1fr)_minmax(380px,44%)]">
          <div className="min-w-0">
            {frequentView ? (
              visible.length === 0 &&
              frequent.data && (
                <Text size="sm" c="dimmed" py="sm">
                  No frequently used facilities yet. Add one from a nearby facility&apos;s row.
                </Text>
              )
            ) : awaitingStateQuery ? (
              <Text size="sm" c="dimmed" py="sm">
                Type a facility name to search all of {patientState}.
              </Text>
            ) : (
              visible.length === 0 && (
                <Text size="sm" c="dimmed" py="sm">
                  {stateWide && coords
                    ? `No imaging facilities named "${trimmedQuery}" found in ${patientState}.`
                    : `No ${isLab ? `${parentLabName} locations` : 'imaging facilities'} found within ${radius === 'state' ? 25 : radius} miles.`}
                </Text>
              )
            )}
            <div ref={listRef} className="relative max-h-[640px] overflow-y-auto pr-1">
              {visible.map((f, i) => (
                <FacilityRow
                  key={f.placeId}
                  rootRef={(el) => {
                    if (el) rowRefs.current.set(f.placeId, el)
                    else rowRefs.current.delete(f.placeId)
                  }}
                  facility={f}
                  index={i + 1}
                  selected={
                    f.placeId === highlighted ||
                    (!!f.organizationId && f.organizationId === selectedFacilityId)
                  }
                  onSelect={() => setHighlighted(f.placeId)}
                  onHover={setHovered}
                  onUse={(fax, saveFax) => void handleUse(f.placeId, fax, saveFax)}
                  adopting={adoptingId === f.placeId}
                  // PD-1929 / PD-1939 — a faxed order can only go out with a number.
                  requireFax
                  labName={parentLabName ?? 'radiology'}
                  onPatientChooses={props.onPatientChoosesLocation}
                  orderFax={
                    f.organizationId && f.organizationId === selectedFacilityId
                      ? props.selectedFacilityFax
                      : undefined
                  }
                  // Radiology only: labs pass none of these, so the row shows no controls.
                  {...(isLab
                    ? {}
                    : frequentView
                      ? {
                          bookmarked: bookmarkedIds.has(f.placeId),
                          bookmarking: bookmarkingIds.has(f.placeId),
                          onBookmark: (bookmarked: boolean) =>
                            void handleBookmark(f.placeId, bookmarked),
                        }
                      : {
                          onRemember: (fax: string) => void handleRemember(f.placeId, fax),
                          remembered: rememberedIds.has(f.placeId),
                          remembering: rememberingIds.has(f.placeId),
                        })}
                />
              ))}
            </div>
            <Group gap="xs" mt="xs">
              {!frequentView && radius === 25 && (
                <Button variant="subtle" size="sm" onClick={() => setRadius(50)}>
                  Widen search to 50 miles
                </Button>
              )}
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconPlus size={14} />}
                onClick={openAddFacilityModal}
              >
                Can&apos;t find your facility? Add a new one
              </Button>
            </Group>
          </div>
          <div className="flex flex-col gap-2 lg:sticky lg:top-2">
            <div className="h-[420px] lg:h-[560px]">
              <FacilityMap
                facilities={visible}
                selectedPlaceId={highlighted}
                hoveredPlaceId={hovered}
                onSelect={setHighlighted}
                patient={coords}
              />
            </div>
            <Text size="xs" className="text-[#8C9AA8]">
              Pins are numbered to match the list. Hovering a row lifts its pin.
            </Text>
          </div>
        </div>
      )}
    </Paper>
  )
}
